import { Component, inject, ChangeDetectionStrategy, computed, input, effect } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { CmsImageData, Location } from '@fcom/core-api';
import { PageMetaService } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { SchemaService } from '@fcom/common/services/schema/schema.service';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { LocationRouteCffService, AppState } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { ContentType } from '@fcom/common/interfaces/gtm.interface';
import { TagTheme } from '@fcom/ui-components/components/tag/interfaces';
import { globalBookingOrigin } from '@fcom/common/store/selectors/global-booking.selectors';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

/**
 * Hero component for destination page
 *
 * @example
 * <fin-destination-hero
 *  [title]=""
 *  [backgroundImage]=""
 *  [destinationCode]=""
 *  [enableAward]=""
 *  [enableAurinkomatkat]=""
 *  [enablePromoCode]=""
 * >
 * </fin-destination-hero>
 */
@Component({
  selector: 'fin-destination-hero',
  templateUrl: './destination-hero.component.html',
  styleUrls: ['./destination-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DestinationHeroComponent {
  private languageService = inject(LanguageService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private pageMetaService = inject(PageMetaService);
  private store$ = inject<Store<AppState>>(Store);
  private schemaService = inject(SchemaService);
  private themeService = inject(FinnairThemeService);

  readonly TripType = TripType;
  readonly CmsTripTypeMap = CmsTripTypeMap;
  readonly TagTheme = TagTheme;

  backgroundImage = input.required<CmsImageData>();
  campaignLabel = input<string>(null);
  campaignEndText = input<string>(null);
  contentType = input<ContentType>(ContentType.SINGLE_DESTINATION);
  destinationCode = input.required<string>();
  enableAurinkomatkat = input<boolean>(false);
  enableAward = input<boolean>(false);
  enableBlackTeaser = input<boolean>(false);
  enablePromoCode = input<boolean>(false);
  isInCampaign = input<boolean>(false);
  title = input.required<string>();

  isNewBrand = computed<boolean>(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  readonly originLocationCodeFromStore = toSignal(this.store$.pipe(globalBookingOrigin()));
  private readonly defaultOriginLocationCode = toSignal(this.pageMetaService.defaultOriginLocationCode$);

  private originQueryCode = computed(() => this.originLocationCodeFromStore() ?? this.defaultOriginLocationCode());
  private originResource = rxResource({
    params: () => this.originQueryCode(),
    stream: ({ params }) => this.locationRouteCffService.bestGuessFor(params, this.languageService.localeValue),
  });
  private destinationResource = rxResource({
    params: () => this.destinationCode(),
    stream: ({ params }) => this.locationRouteCffService.bestGuessFor(params, this.languageService.localeValue),
  });
  readonly origin = computed<Location | undefined>(() => this.originResource.value());
  readonly destination = computed<Location | undefined>(() => this.destinationResource.value());

  readonly originLocationCode = computed(
    () => this.originLocationCodeFromStore() ?? this.origin()?.locationCode ?? this.defaultOriginLocationCode()
  );
  readonly selectedOriginLocationName = computed(() => this.origin()?.cityName ?? this.defaultOriginLocationCode());

  readonly showOneWayPrice = computed(
    () => this.origin()?.continentCode === 'eu' && this.destination()?.continentCode === 'eu'
  );

  constructor() {
    effect(() => {
      const origin = this.origin();
      const destination = this.destination();
      if (!origin || !destination) {
        return;
      }
      this.schemaService.addFlightSchema({
        departureAirportName: origin.title,
        departureAirportIataCode: origin.locationCode,
        arrivalAirportName: destination.title,
        arrivalAirportIataCode: destination.locationCode,
      });
    });
  }
}
